// Current user management (identity + private session persistence).
import { createContext, useContext, useEffect, useState } from "react";
import { getUsers, login, setupPin, validateSession } from "../lib/api";
import { STORAGE_TOKEN, STORAGE_USER } from "../config";

const UserContext = createContext(null);
export const useUser = () => useContext(UserContext);

export function UserProvider({ children }) {
  const [users, setUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(
    () => localStorage.getItem(STORAGE_USER) || null
  );
  const [loading, setLoading] = useState(true);

  const refreshUsers = async () => {
    const loadedUsers = await getUsers();
    setUsers(loadedUsers);
    return loadedUsers;
  };

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        const loadedUsers = await getUsers();
        if (cancelled) return;
        setUsers(loadedUsers);

        const savedUserId = localStorage.getItem(STORAGE_USER);
        const savedToken = localStorage.getItem(STORAGE_TOKEN);
        if (savedUserId && savedToken) {
          try {
            await validateSession(savedUserId);
          } catch (e) {
            localStorage.removeItem(STORAGE_USER);
            localStorage.removeItem(STORAGE_TOKEN);
            if (!cancelled) setCurrentUserId(null);
          }
        } else if (savedUserId || savedToken) {
          localStorage.removeItem(STORAGE_USER);
          localStorage.removeItem(STORAGE_TOKEN);
          if (!cancelled) setCurrentUserId(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveSession = (id, result) => {
    localStorage.setItem(STORAGE_TOKEN, result.token);
    localStorage.setItem(STORAGE_USER, id);
    setCurrentUserId(id);
  };

  const authenticateUser = async (id, pin) => {
    const result = await login({ userId: id, pin });
    saveSession(id, result);
    return result;
  };

  const createPrivatePin = async (id, pin) => {
    const result = await setupPin({ userId: id, pin });
    saveSession(id, result);
    await refreshUsers();
    return result;
  };

  const switchUser = () => {
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_TOKEN);
    setCurrentUserId(null);
  };

  const currentUser = users.find((u) => u.id === currentUserId) || null;

  return (
    <UserContext.Provider
      value={{
        users,
        currentUserId,
        currentUser,
        authenticateUser,
        createPrivatePin,
        switchUser,
        loading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
